/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, Input, effect, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './folio-information-page.component.html';

import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';
import {CreativeSetApiService, CreativeSetModel} from '../creative-set/creative-set-api.service';
import {SignalAdminResourceListState, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {CreativeApiService, CreativeModel} from '../creative/creative-api.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';
import {CreativeModule} from '../creative/creative.module';
import {CreativeSetListPageComponent} from '../creative-set/creative-set-list-page.component';

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule, CreativeModule, CreativeSetListPageComponent],
  selector: 'adn-folio-information-page',
  template
})
export class FolioInformationPageComponent {
  private readonly modelState = signal<any>(null);
  readonly effectiveIsNew = signal(false);
  readonly readOnlyState = signal(false);

  @Input()
  set model(model: any) {
    this.modelState.set(model);
  }

  @Input()
  set isNew(isNew: boolean) {
    this.effectiveIsNew.set(!!isNew);
  }

  @Input()
  set readOnly(readOnly: boolean) {
    this.readOnlyState.set(!!readOnly);
  }

  readonly activeTab = signal<'creativeSets' | 'creatives'>('creativeSets');
  readonly showCreatives: boolean;

  readonly creativeSets = signal<SignalAdminResourceListState<CreativeSetModel> | null>(null);
  readonly creatives = signal<SignalAdminResourceListState<CreativeModel> | null>(null);

  private listKey = '';
  private readonly listFactory = inject(SignalAdminResourceListStateFactory);
  private readonly creativeSetApi = inject(CreativeSetApiService);
  private readonly creativeApi = inject(CreativeApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly listEffect = effect(() => {
    this.initLists(this.modelState(), this.effectiveIsNew(), this.readOnlyState());
  });

  constructor(
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly $state: StateService
  ) {
    this.showCreatives = !!LocalUserPermissions.getSectionPerms().creatives;
  }

  selectTab(tab: ReturnType<FolioInformationPageComponent['activeTab']>): void {
    this.activeTab.set(tab);
  }

  loadExternalCreativeSet(): void {
    const model = this.modelState();
    this.$state.go('app.adv.creative-sets.new', {
      folioId: model.id,
      folioName: model.name,
      advertiserId: _.get(model, ['advertiser', 'id']),
      advertiserName: _.get(model, ['advertiser', 'name'])
    });
  }

  newBulkCreative(): void {
    const model = this.modelState();
    this.$state.go('app.adv.creatives.bulkupload', _.assign({}, advertiserNavigationParams(model.advertiser), {
      folioId: model.id,
      folioName: model.name
    }));
  }

  newCreative(): void {
    const model = this.modelState();
    this.$state.go('app.adv.creatives.new', _.assign({}, advertiserNavigationParams(model.advertiser), {
      folioId: model.id,
      folioName: model.name
    }));
  }

  private initLists(model: any, isNew: boolean, readOnly: boolean): void {
    if (!model?.id || isNew) {
      this.listKey = '';
      this.creativeSets.set(null);
      this.creatives.set(null);
      return;
    }

    const listKey = model.id;
    if (this.listKey === listKey) {
      this.creativeSets()?.readOnly.set(readOnly);
      this.creatives()?.readOnly.set(readOnly);
      return;
    }
    this.listKey = listKey;

    const creativeSets = this.listFactory.create(this.creativeSetApi, {
      listId: 'fullCreativeSetsForFolio',
      baseTable: 'creativeSet',
      syncPageToUrl: false,
      additionalParams: {folio: model.id}
    }, this.destroyRef);
    creativeSets.readOnly.set(readOnly);
    creativeSets.load();
    this.creativeSets.set(creativeSets);

    const creatives = this.listFactory.create(this.creativeApi, {
      listId: 'fullCreativesForFolio', baseTable: 'creative', syncPageToUrl: false,
      additionalParams: {folio: model.id}
    }, this.destroyRef);
    creatives.readOnly.set(readOnly);
    creatives.load();
    this.creatives.set(creatives);
  }

}
